
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Plus, Edit, Trash2, ArrowUp, ArrowDown } from "lucide-react";

interface DailyTip {
  id: string;
  text: string;
  category: string;
  color: string;
  is_active: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

const colorOptions = [
  { value: "bg-blue-100", label: "Blue" },
  { value: "bg-purple-100", label: "Purple" },
  { value: "bg-green-100", label: "Green" },
  { value: "bg-pink-100", label: "Pink" },
  { value: "bg-yellow-100", label: "Yellow" },
  { value: "bg-orange-100", label: "Orange" },
  { value: "bg-red-100", label: "Red" },
  { value: "bg-indigo-100", label: "Indigo" },
];

const DailyTipsManager = () => {
  const [tips, setTips] = useState<DailyTip[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTip, setEditingTip] = useState<DailyTip | null>(null);
  const [formData, setFormData] = useState({
    text: "",
    category: "",
    color: "bg-blue-100",
    is_active: true,
    display_order: 0
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchTips();
  }, []);

  const fetchTips = async () => {
    try {
      const { data, error } = await supabase
        .from('daily_tips')
        .select('*')
        .order('display_order', { ascending: true });

      if (error) throw error;
      setTips(data || []);
    } catch (error) {
      console.error('Error fetching tips:', error);
      toast({
        title: "Error",
        description: "Failed to fetch daily tips",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (editingTip) {
        const { error } = await supabase
          .from('daily_tips')
          .update(formData)
          .eq('id', editingTip.id);
        
        if (error) throw error;
        
        toast({
          title: "Success",
          description: "Daily tip updated successfully",
        });
      } else {
        const { error } = await supabase
          .from('daily_tips')
          .insert([formData]);
        
        if (error) throw error;
        
        toast({
          title: "Success",
          description: "Daily tip created successfully",
        });
      }
      
      resetForm();
      setDialogOpen(false);
      fetchTips();
    } catch (error) {
      console.error('Error saving tip:', error);
      toast({
        title: "Error",
        description: "Failed to save daily tip",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this tip?')) return;
    
    try {
      const { error } = await supabase
        .from('daily_tips')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      toast({
        title: "Success",
        description: "Daily tip deleted successfully",
      });
      
      fetchTips();
    } catch (error) {
      console.error('Error deleting tip:', error);
      toast({
        title: "Error",
        description: "Failed to delete daily tip",
        variant: "destructive",
      });
    }
  };

  const updateOrder = async (id: string, newOrder: number) => {
    try {
      const { error } = await supabase
        .from('daily_tips')
        .update({ display_order: newOrder })
        .eq('id', id);
      
      if (error) throw error;
      fetchTips();
    } catch (error) {
      console.error('Error updating order:', error);
      toast({
        title: "Error",
        description: "Failed to update tip order",
        variant: "destructive",
      });
    }
  };

  const resetForm = () => {
    setFormData({
      text: "",
      category: "",
      color: "bg-blue-100",
      is_active: true,
      display_order: tips.length + 1
    });
    setEditingTip(null);
  };

  const openEditDialog = (tip: DailyTip) => {
    setEditingTip(tip);
    setFormData({
      text: tip.text,
      category: tip.category,
      color: tip.color,
      is_active: tip.is_active,
      display_order: tip.display_order
    });
    setDialogOpen(true);
  };

  if (loading) {
    return <div>Loading daily tips...</div>;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Daily Tips Management</CardTitle>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={resetForm}>
                <Plus className="w-4 h-4 mr-2" />
                Add New Tip
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>
                  {editingTip ? 'Edit Daily Tip' : 'Add New Daily Tip'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="category">Category</Label>
                  <Input
                    id="category"
                    value={formData.category}
                    onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                    placeholder="e.g., Math, Alphabet, Organization"
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="text">Tip Text</Label>
                  <Textarea
                    id="text"
                    value={formData.text}
                    onChange={(e) => setFormData(prev => ({ ...prev, text: e.target.value }))}
                    placeholder="Enter the learning tip..."
                    rows={4}
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="color">Background Color</Label>
                  <Select
                    value={formData.color}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, color: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {colorOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          <div className="flex items-center gap-2">
                            <div className={`w-4 h-4 rounded ${option.value}`}></div>
                            {option.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="display_order">Display Order</Label>
                  <Input
                    id="display_order"
                    type="number"
                    min="1"
                    value={formData.display_order}
                    onChange={(e) => setFormData(prev => ({ ...prev, display_order: parseInt(e.target.value) || 1 }))}
                  />
                </div>
                
                <div className="flex items-center space-x-2">
                  <Switch
                    id="is_active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                  />
                  <Label htmlFor="is_active">Active</Label>
                </div>
                
                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit">
                    {editingTip ? 'Update' : 'Create'} Tip
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Order</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Text</TableHead>
              <TableHead>Color</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tips.map((tip, index) => (
              <TableRow key={tip.id}>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <span className="w-8">{tip.display_order}</span>
                    <div className="flex flex-col gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => updateOrder(tip.id, tip.display_order - 1)}
                        disabled={index === 0}
                      >
                        <ArrowUp className="w-3 h-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => updateOrder(tip.id, tip.display_order + 1)}
                        disabled={index === tips.length - 1}
                      >
                        <ArrowDown className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </TableCell>
                <TableCell>{tip.category}</TableCell>
                <TableCell className="max-w-md truncate" title={tip.text}>
                  {tip.text}
                </TableCell>
                <TableCell>
                  <div className={`w-6 h-6 rounded ${tip.color}`}></div>
                </TableCell>
                <TableCell>
                  <span className={`px-2 py-1 rounded text-xs ${
                    tip.is_active 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {tip.is_active ? 'Active' : 'Inactive'}
                  </span>
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openEditDialog(tip)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(tip.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default DailyTipsManager;
